/**
 * GitHub Settings — configure and manage GitHub sync for the organization.
 */

import React, { useEffect, useState, useCallback } from "react";
import * as api from "../api.js";
import { PageHeader, Loading, ErrorBanner } from "../components.jsx";

export default function GitHubSettings() {
  const [config, setConfig] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [pushing, setPushing] = useState(false);
  const [pushResult, setPushResult] = useState(null);
  const [pulling, setPulling] = useState(false);
  const [pullResult, setPullResult] = useState(null);
  const [settingsTab, setSettingsTab] = useState("config"); // "config" | "tenants" | "pull-log"
  const [pullLog, setPullLog] = useState([]);
  const [logLoading, setLogLoading] = useState(false);
  const [tenantList, setTenantList] = useState([]);
  const [subTenantMap, setSubTenantMap] = useState({});

  // Form state
  const [repoUrl, setRepoUrl] = useState("");
  const [branch, setBranch] = useState("main");
  const [token, setToken] = useState("");
  const [enabled, setEnabled] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cfgRes, statusRes] = await Promise.all([
        api.getGitHubConfig(),
        api.getGitHubStatus().catch(() => ({ data: { ok: false } })),
      ]);
      const cfg = cfgRes.data || {};
      setConfig(cfg);
      setRepoUrl(cfg.repo || "");
      setBranch(cfg.branch || "main");
      setToken("");  // Never show existing token
      setEnabled(cfg.enabled || false);
      setStatus(statusRes.data || null);
      setError(null);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const body = { repo: repoUrl, branch, enabled };
      if (token) body.token = token;  // Only send if user entered a new one
      await api.saveGitHubConfig(body);
      setToken("");
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  };

  const testConn = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await api.testGitHubConnection();
      setTestResult(res.data);
    } catch (err) {
      setTestResult({ ok: false, error: err.message });
    } finally {
      setTesting(false);
    }
  };

  const loadTenants = async () => {
    try {
      const res = await api.listTenants();
      const tList = res.data || [];
      setTenantList(tList);
      const map = {};
      for (const t of tList) {
        try {
          const subRes = await api.listSubTenants(t._id);
          map[t._id] = subRes.data || [];
        } catch { map[t._id] = []; }
      }
      setSubTenantMap(map);
    } catch {}
  };

  const push = async (scope = "all", tenantId, subTenantId) => {
    const scopeLabel = scope === "library" ? "library" : scope === "tenant" ? `tenant ${subTenantId || tenantId}` : "all content";
    if (!window.confirm(`Push ${scopeLabel} to GitHub?`)) return;
    setPushing(true);
    setPushResult(null);
    try {
      await api.pushToGitHub({ scope, tenantId, subTenantId }); // starts background push
      // Poll for completion
      const poll = async () => {
        try {
          const statusRes = await api.getGitHubPushStatus();
          const s = statusRes.data;
          if (s.status === "complete") {
            setPushResult(s.result);
            setPushing(false);
            await load();
            await loadTenants();
          } else if (s.status === "error") {
            setPushResult({ error: s.error });
            setPushing(false);
          } else {
            setTimeout(poll, 2000); // check every 2 seconds
          }
        } catch {
          setPushing(false);
        }
      };
      setTimeout(poll, 2000);
    } catch (err) {
      setPushResult({ error: err.message });
      setPushing(false);
    }
  };

  const pull = async () => {
    if (!window.confirm("Pull changes from GitHub? Library changes go to live, tenant changes go to draft.")) return;
    setPulling(true);
    setPullResult(null);
    try {
      const res = await api.pullFromGitHub();
      setPullResult(res.data);
      await load();
    } catch (err) {
      setPullResult({ error: err.message });
    } finally {
      setPulling(false);
    }
  };

  const loadPullLog = async () => {
    setLogLoading(true);
    try {
      const res = await api.getGitHubPullLog();
      setPullLog(res.data || []);
    } catch {} finally {
      setLogLoading(false);
    }
  };

  if (loading) return <Loading />;

  return (
    <>
      <PageHeader
        title="GitHub Integration"
        subtitle="Sync prompts, configs, and post-call actions with a GitHub repository."
        breadcrumbs={[{ label: "Settings" }, { label: "GitHub" }]}
      />

      <div className="page-body">
        <ErrorBanner error={error} />

        {/* Tab bar */}
        <div className="tabs mb-16" style={{ borderBottom: "1px solid var(--color-border)" }}>
          {[
            { id: "config", label: "Configuration" },
            { id: "tenants", label: "Tenant Management" },
            { id: "pull-log", label: "Pull History" },
          ].map((t) => (
            <button
              key={t.id}
              className={`tab ${settingsTab === t.id ? "active" : ""}`}
              onClick={() => { setSettingsTab(t.id); if (t.id === "pull-log") loadPullLog(); if (t.id === "tenants") loadTenants(); }}
              style={{
                padding: "8px 16px", background: "none", border: "none",
                borderBottom: settingsTab === t.id ? "2px solid var(--color-primary)" : "2px solid transparent",
                cursor: "pointer", fontWeight: settingsTab === t.id ? 600 : 400,
                color: settingsTab === t.id ? "var(--color-primary)" : "var(--color-text-muted)", fontSize: 13,
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {settingsTab === "pull-log" && (
          <div>
            {logLoading ? <Loading /> : pullLog.length === 0 ? (
              <div className="empty-state" style={{ padding: 40 }}>
                <h3>No pull history</h3>
                <p className="muted">Pull from GitHub to see change history here.</p>
              </div>
            ) : (
              pullLog.map((entry, idx) => (
                <div key={entry._id || idx} className="card" style={{ marginBottom: 12 }}>
                  <div className="card-header" style={{ justifyContent: "space-between" }}>
                    <div>
                      <strong style={{ fontSize: 13 }}>
                        Pull #{pullLog.length - idx}
                      </strong>
                      <span className="mono muted small" style={{ marginLeft: 8 }}>
                        {entry.sha?.slice(0, 8)}
                      </span>
                      <span className="muted small" style={{ marginLeft: 8 }}>
                        {new Date(entry.pulled_at).toLocaleString()}
                      </span>
                      <span className="muted small" style={{ marginLeft: 8 }}>
                        by {entry.pulled_by}
                      </span>
                    </div>
                    <div style={{ display: "flex", gap: 6 }}>
                      <span className="badge badge-live" style={{ fontSize: 10 }}>{entry.applied_count} applied</span>
                      {entry.skipped_count > 0 && <span className="badge badge-neutral" style={{ fontSize: 10 }}>{entry.skipped_count} unchanged</span>}
                      {entry.error_count > 0 && <span className="badge badge-danger" style={{ fontSize: 10 }}>{entry.error_count} errors</span>}
                    </div>
                  </div>

                  {(entry.changes || []).length > 0 && (
                    <div style={{ fontSize: 12 }}>
                      {entry.changes.map((c, ci) => (
                        <details key={ci} style={{ borderBottom: "1px solid var(--color-border)" }}>
                          <summary style={{ padding: "6px 12px", cursor: "pointer", display: "flex", alignItems: "center", gap: 8 }}>
                            <span style={{
                              fontSize: 10, fontWeight: 700, padding: "1px 5px", borderRadius: 3,
                              background: c.type === "added" ? "#e8f5e9" : c.type === "deleted_from_repo" ? "#ffebee" : "#e3f2fd",
                              color: c.type === "added" ? "#2e7d32" : c.type === "deleted_from_repo" ? "#c62828" : "#1565c0",
                            }}>
                              {c.type === "added" ? "NEW" : c.type === "deleted_from_repo" ? "DEL" : "MOD"}
                            </span>
                            <span className="mono" style={{ fontSize: 11 }}>{c.path}</span>
                            <span className="muted" style={{ fontSize: 10, marginLeft: "auto" }}>
                              {c.type === "modified" && (
                                <>
                                  <span style={{ color: "#2e7d32" }}>+{c.lines_added || 0}</span>
                                  {" / "}
                                  <span style={{ color: "#c62828" }}>-{c.lines_removed || 0}</span>
                                  {" lines"}
                                </>
                              )}
                              {c.type === "added" && <>{c.new_lines} lines, {c.new_chars} chars</>}
                            </span>
                            <span className={`badge ${c.scope === "library" ? "badge-accent" : "badge-draft"}`} style={{ fontSize: 9, padding: "0 4px" }}>
                              {c.scope === "library" ? "live" : "draft"}
                            </span>
                          </summary>
                          <div style={{ padding: "4px 12px 8px", fontFamily: "var(--font-mono)", fontSize: 11 }}>
                            {c.old_preview && (
                              <div style={{ marginBottom: 6 }}>
                                <strong style={{ color: "#888", fontSize: 10 }}>BEFORE (preview)</strong>
                                <pre style={{ margin: "2px 0", padding: 6, background: "#ffeef0", borderRadius: 4, fontSize: 10, whiteSpace: "pre-wrap", maxHeight: 120, overflow: "auto" }}>
{c.old_preview}{c.old_chars > 500 ? "\n... (truncated)" : ""}</pre>
                              </div>
                            )}
                            <div>
                              <strong style={{ color: "#888", fontSize: 10 }}>AFTER (preview)</strong>
                              <pre style={{ margin: "2px 0", padding: 6, background: "#e6ffec", borderRadius: 4, fontSize: 10, whiteSpace: "pre-wrap", maxHeight: 120, overflow: "auto" }}>
{c.new_preview}{c.new_chars > 500 ? "\n... (truncated)" : ""}</pre>
                            </div>
                          </div>
                        </details>
                      ))}
                    </div>
                  )}

                  {(entry.errors || []).length > 0 && (
                    <div style={{ padding: "6px 12px", background: "#ffebee", fontSize: 11 }}>
                      <strong style={{ color: "#c62828" }}>Errors:</strong>
                      {entry.errors.map((e, ei) => (
                        <div key={ei} className="mono" style={{ color: "#c62828" }}>{e.path}: {e.error}</div>
                      ))}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}

        {settingsTab === "tenants" && (
          <div>
            <div className="card mb-16">
              <div className="card-header" style={{ justifyContent: "space-between" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <h3 style={{ margin: 0 }}>Library</h3>
                  {config?.github_library_managed ? (
                    <span style={{ color: "var(--color-success)", fontWeight: 600, fontSize: 12 }}>Managed by GitHub</span>
                  ) : (
                    <span className="muted small">Not pushed</span>
                  )}
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                  {!config?.github_library_managed ? (
                    <button
                      className="btn btn-sm primary"
                      disabled={pushing || !config?.has_token}
                      onClick={() => push("library")}
                    >
                      {pushing ? "Pushing..." : "Push Library to GitHub"}
                    </button>
                  ) : (
                    <button
                      className="btn btn-sm"
                      onClick={async () => {
                        if (!window.confirm("Unlock library for GUI editing?")) return;
                        await api.saveGitHubConfig({ github_library_managed: false });
                        await load();
                      }}
                    >
                      Unlock for GUI
                    </button>
                  )}
                </div>
              </div>
              <p className="muted small">
                {config?.github_library_managed
                  ? "Library prompts, protocols, tools, and platform rules are read-only in the admin UI. Edit on GitHub and pull."
                  : "Push library content to GitHub to manage it from there. Once pushed, the admin UI becomes read-only for library content."}
              </p>
            </div>

            {tenantList.length === 0 ? (
              <div className="empty-state" style={{ padding: 30 }}>
                <p className="muted">No tenants found. Create a tenant first.</p>
              </div>
            ) : (
              tenantList.map((tenant) => {
                const subs = subTenantMap[tenant._id] || [];
                return (
                  <div key={tenant._id} className="card mb-16">
                    <div className="card-header" style={{ justifyContent: "space-between" }}>
                      <div>
                        <h3 style={{ margin: 0 }}>{tenant.name}</h3>
                        <span className="mono muted small">{tenant._id}</span>
                      </div>
                    </div>
                    {subs.length === 0 ? (
                      <p className="muted small" style={{ padding: "8px 0" }}>No sub-tenants</p>
                    ) : (
                      <table className="table" style={{ border: "none", fontSize: 13 }}>
                        <thead>
                          <tr>
                            <th>Sub-Tenant</th>
                            <th>Specialty</th>
                            <th style={{ width: 120 }}>GitHub Status</th>
                            <th style={{ width: 180 }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {subs.map((sub) => (
                            <tr key={sub._id}>
                              <td><strong>{sub.name || sub.display_name}</strong><div className="mono muted small">{sub._id}</div></td>
                              <td><span className="badge">{sub.specialty}</span></td>
                              <td>
                                {sub.github_managed ? (
                                  <span style={{ color: "var(--color-success)", fontWeight: 600, fontSize: 12 }}>
                                    Managed by GitHub
                                  </span>
                                ) : (
                                  <span className="muted small">Not pushed</span>
                                )}
                              </td>
                              <td>
                                {!sub.github_managed ? (
                                  <button
                                    className="btn btn-sm primary"
                                    disabled={pushing || !config?.has_token}
                                    onClick={() => push("tenant", tenant._id, sub._id)}
                                  >
                                    Push to GitHub
                                  </button>
                                ) : (
                                  <button
                                    className="btn btn-sm"
                                    onClick={async () => {
                                      if (!window.confirm(`Remove GitHub management for ${sub.name || sub._id}? This will allow editing from the admin UI again.`)) return;
                                      try {
                                        await api.updateSubTenant(tenant._id, sub._id, { github_managed: false });
                                      } catch {}
                                      await loadTenants();
                                    }}
                                  >
                                    Unlock for GUI
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                );
              })
            )}

            {pushResult && (
              <div style={{
                padding: "8px 12px", borderRadius: 4, fontSize: 12, marginTop: 12,
                background: pushResult.error ? "#ffebee" : "#e8f5e9",
                border: `1px solid ${pushResult.error ? "#ef9a9a" : "#a5d6a7"}`,
              }}>
                {pushResult.error
                  ? `Push failed: ${pushResult.error}`
                  : `Pushed ${pushResult.files_pushed} files (${pushResult.sha?.slice(0, 8)})`}
                {pushResult.url && <a href={pushResult.url} target="_blank" rel="noopener" style={{ marginLeft: 8 }}>View on GitHub</a>}
              </div>
            )}
          </div>
        )}

        {settingsTab === "config" && (<>
        {/* Configuration */}
        <div className="card mb-16">
          <div className="card-header">
            <h3>Repository Configuration</h3>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <label style={{ fontSize: 12, fontWeight: 600 }}>
                <input
                  type="checkbox"
                  checked={enabled}
                  onChange={(e) => setEnabled(e.target.checked)}
                  style={{ marginRight: 6 }}
                />
                Enable GitHub Sync
              </label>
            </div>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <label style={{ fontSize: 13, fontWeight: 600, minWidth: 120 }}>Repository</label>
              <input
                type="text"
                value={repoUrl}
                onChange={(e) => setRepoUrl(e.target.value)}
                placeholder="owner/repo-name"
                style={{ flex: 1, padding: 8, border: "1px solid var(--color-border)", borderRadius: "var(--radius-sm)", fontSize: 13, fontFamily: "monospace" }}
              />
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <label style={{ fontSize: 13, fontWeight: 600, minWidth: 120 }}>Branch</label>
              <input
                type="text"
                value={branch}
                onChange={(e) => setBranch(e.target.value)}
                placeholder="main"
                style={{ width: 200, padding: 8, border: "1px solid var(--color-border)", borderRadius: "var(--radius-sm)", fontSize: 13, fontFamily: "monospace" }}
              />
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <label style={{ fontSize: 13, fontWeight: 600, minWidth: 120 }}>Access Token</label>
              <input
                type="password"
                value={token}
                onChange={(e) => setToken(e.target.value)}
                placeholder={config?.has_token ? "Token saved (enter new to replace)" : "ghp_xxxx..."}
                style={{ flex: 1, padding: 8, border: "1px solid var(--color-border)", borderRadius: "var(--radius-sm)", fontSize: 13, fontFamily: "monospace" }}
              />
              {config?.has_token && <span style={{ fontSize: 11, color: "var(--color-success)" }}>Saved</span>}
            </div>
            <p className="muted small">
              Create a <a href="https://github.com/settings/tokens/new?scopes=repo" target="_blank" rel="noopener">Personal Access Token</a> with <code>repo</code> scope.
            </p>
          </div>

          <div className="btn-row" style={{ marginTop: 16 }}>
            <button className="btn primary sm" onClick={save} disabled={saving}>
              {saving ? "Saving..." : "Save Configuration"}
            </button>
            <button className="btn sm" onClick={testConn} disabled={testing || !config?.has_token}>
              {testing ? "Testing..." : "Test Connection"}
            </button>
            {config?.has_token && !testResult?.ok && (
              <button
                className="btn sm"
                onClick={async () => {
                  if (!window.confirm("Create a new private GitHub repository for this organization?")) return;
                  const name = repoUrl || undefined; // let server use default Configural-{OrgName}
                  setTesting(true);
                  try {
                    const res = await api.createGitHubRepo({ repo: name, private: true });
                    if (res.data?.ok) {
                      setTestResult({ ok: true, repo: res.data.full_name, private: res.data.private, created: true });
                      setRepoUrl(res.data.full_name);
                      await load();
                    } else {
                      setTestResult({ ok: false, error: res.data?.error, exists: res.data?.exists });
                    }
                  } catch (err) {
                    setTestResult({ ok: false, error: err.message });
                  } finally {
                    setTesting(false);
                  }
                }}
                disabled={testing}
              >
                Create Repo
              </button>
            )}
          </div>

          {testResult && (
            <div style={{
              marginTop: 12, padding: "8px 12px", borderRadius: 4, fontSize: 12,
              background: testResult.ok ? "#e8f5e9" : "#ffebee",
              border: `1px solid ${testResult.ok ? "#a5d6a7" : "#ef9a9a"}`,
              color: testResult.ok ? "#2e7d32" : "#c62828",
            }}>
              {testResult.ok
                ? testResult.created
                  ? <>Repo created: <strong>{testResult.repo}</strong> ({testResult.private ? "private" : "public"}). Configuration saved automatically.</>
                  : `Connected to ${testResult.repo} (${testResult.private ? "private" : "public"})`
                : testResult.exists
                  ? "Repository already exists. Enter it in the Repository field above and save."
                  : `Connection failed: ${testResult.error}`}
            </div>
          )}
        </div>

        {/* Pull from GitHub */}
        {config?.has_token && (
          <div className="card mb-16">
            <div className="card-header" style={{ justifyContent: "space-between" }}>
              <h3>Pull from GitHub</h3>
              <button className="btn sm" style={{ background: "#2e7d32", color: "#fff" }} onClick={pull} disabled={pulling}>
                {pulling ? "Pulling..." : "Pull from GitHub"}
              </button>
            </div>
            <p className="muted small">
              Import changes from the GitHub repo. Library changes go directly to live.
              Tenant changes go to draft — publish to live from the sub-tenant's Revisions tab.
              Use the Tenant Management tab to push individual tenants to GitHub.
            </p>

            {pullResult && (
              <div style={{
                padding: "8px 12px", borderRadius: 4, fontSize: 12, marginTop: 12,
                background: pullResult.errors?.length ? "#fff3e0" : "#e8f5e9",
                border: `1px solid ${pullResult.errors?.length ? "#ffe082" : "#a5d6a7"}`,
              }}>
                <strong>Pull complete:</strong>{" "}
                {pullResult.applied?.length || 0} applied,{" "}
                {pullResult.skipped?.length || 0} unchanged,{" "}
                {pullResult.errors?.length || 0} errors
                {pullResult.applied?.length > 0 && (
                  <details style={{ marginTop: 6 }}>
                    <summary style={{ cursor: "pointer", fontSize: 11 }}>Applied files</summary>
                    <ul style={{ margin: "4px 0", paddingLeft: 20, fontSize: 11 }}>
                      {pullResult.applied.map((f, i) => <li key={i}>{f}</li>)}
                    </ul>
                  </details>
                )}
                {pullResult.errors?.length > 0 && (
                  <details style={{ marginTop: 6 }}>
                    <summary style={{ cursor: "pointer", fontSize: 11, color: "#c62828" }}>Errors</summary>
                    <ul style={{ margin: "4px 0", paddingLeft: 20, fontSize: 11, color: "#c62828" }}>
                      {pullResult.errors.map((e, i) => <li key={i}>{e.path}: {e.error}</li>)}
                    </ul>
                  </details>
                )}
              </div>
            )}
          </div>
        )}

        {/* Rate Limits */}
        {config?.has_token && (
          <RateLimitCard />
        )}

        {/* Sync Status */}
        {status?.configured && (
          <div className="card">
            <div className="card-header">
              <h3>Sync Status</h3>
            </div>
            <table className="table" style={{ border: "none", fontSize: 13 }}>
              <tbody>
                <tr>
                  <th style={{ width: "30%" }}>Repository</th>
                  <td className="mono">
                    <a href={`https://github.com/${config?.repo}`} target="_blank" rel="noopener">{config?.repo}</a>
                  </td>
                </tr>
                <tr>
                  <th>Branch</th>
                  <td className="mono">{config?.branch || "main"}</td>
                </tr>
                <tr>
                  <th>Enabled</th>
                  <td>{enabled ? <span style={{ color: "var(--color-success)", fontWeight: 600 }}>Yes</span> : <span className="muted">No</span>}</td>
                </tr>
                <tr>
                  <th>Last Push</th>
                  <td>{config?.last_push ? new Date(config.last_push).toLocaleString() : "Never"}</td>
                </tr>
                <tr>
                  <th>Last Pull</th>
                  <td>{config?.last_pull ? new Date(config.last_pull).toLocaleString() : "Never"}</td>
                </tr>
                {status?.latest_sha && (
                  <tr>
                    <th>Latest Commit</th>
                    <td>
                      <span className="mono" style={{ fontSize: 11 }}>{status.latest_sha?.slice(0, 8)}</span>
                      <span className="muted small" style={{ marginLeft: 8 }}>{status.latest_message}</span>
                      {status.is_behind && (
                        <span className="badge badge-stale" style={{ marginLeft: 8, fontSize: 10 }}>
                          New commits available
                        </span>
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
        </>)}
      </div>
    </>
  );
}

function RateLimitCard() {
  const [limits, setLimits] = useState(null);
  const [checking, setChecking] = useState(false);

  const check = async () => {
    setChecking(true);
    try {
      const res = await api.getGitHubRateLimits();
      setLimits(res.data);
    } catch {}
    setChecking(false);
  };

  const core = limits?.core;
  const pct = core?.percent_remaining || 0;
  const barColor = pct > 50 ? "#43a047" : pct > 15 ? "#f57f17" : "#c62828";

  return (
    <div className="card mb-16">
      <div className="card-header" style={{ justifyContent: "space-between" }}>
        <h3>API Rate Limits</h3>
        <button className="btn btn-sm" onClick={check} disabled={checking} style={{ fontSize: 11 }}>
          {checking ? "Checking..." : limits ? "Refresh" : "Check Rate Limits"}
        </button>
      </div>

      {!limits ? (
        <p className="muted small">Click "Check Rate Limits" to see your current GitHub API usage.</p>
      ) : !limits.ok ? (
        <p style={{ color: "#c62828", fontSize: 13 }}>Error: {limits.error}</p>
      ) : (
        <>
          {/* Progress bar */}
          <div style={{ marginBottom: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 4 }}>
              <span><strong>{core.remaining}</strong> / {core.limit} requests remaining</span>
              <span style={{ color: barColor, fontWeight: 600 }}>{pct}%</span>
            </div>
            <div style={{ height: 8, background: "#eee", borderRadius: 4, overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${pct}%`, background: barColor, borderRadius: 4, transition: "width 0.3s" }} />
            </div>
          </div>

          {/* Status */}
          <div style={{
            padding: "8px 12px", borderRadius: 4, fontSize: 13,
            background: limits.can_push ? "#e8f5e9" : "#ffebee",
            border: `1px solid ${limits.can_push ? "#a5d6a7" : "#ef9a9a"}`,
            color: limits.can_push ? "#2e7d32" : "#c62828",
            fontWeight: 600, marginBottom: 8,
          }}>
            {limits.can_push ? "✓ " : "✗ "}{limits.recommendation}
          </div>

          {/* Details */}
          <table className="table" style={{ border: "none", fontSize: 12 }}>
            <tbody>
              <tr>
                <th style={{ width: "30%" }}>Used</th>
                <td>{core.used} of {core.limit}</td>
              </tr>
              <tr>
                <th>Resets at</th>
                <td>{new Date(core.reset_at).toLocaleString()} ({core.minutes_until_reset} min from now)</td>
              </tr>
              <tr>
                <th>Search API</th>
                <td>{limits.search?.remaining} / {limits.search?.limit} remaining</td>
              </tr>
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}
